import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Users, Globe2, KeyRound, Copy, Lock } from "lucide-react";import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

export default function Lobby() {
  const nav = useNavigate();
  const [publicRooms, setPublicRooms] = useState([]);
  const [myRooms, setMyRooms] = useState([]);
  const [loading, setLoading] = useState(true);

  // create mini
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", description: "", topic: "" });

  // join
  const [joinOpen, setJoinOpen] = useState(false);
  const [joinCode, setJoinCode] = useState("");
  const [joining, setJoining] = useState(false);

  // access code dialog (for code-gated public rooms like DownLow)
  const [codeOpen, setCodeOpen] = useState(false);
  const [codeRoom, setCodeRoom] = useState(null);
  const [codeInput, setCodeInput] = useState("");
  const [codeSubmitting, setCodeSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, m] = await Promise.all([api.get("/rooms/public"), api.get("/rooms/mine")]);
      setPublicRooms(p.data);
      setMyRooms(m.data);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createMini = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await api.post("/rooms/mini", createForm);
      toast.success(`Mini room created · Code ${data.invite_code}`);
      setCreateOpen(false);
      setCreateForm({ name: "", description: "", topic: "" });
      nav(`/room/${data.id}`);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setCreating(false);
    }
  };

  const joinMini = async (e) => {
    e.preventDefault();
    setJoining(true);
    try {
      const { data } = await api.post(`/rooms/join/${joinCode.trim().toUpperCase()}`);
      toast.success(`Joined: ${data.name}`);
      setJoinOpen(false);
      setJoinCode("");
      nav(`/room/${data.id}`);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setJoining(false);
    }
  };

  const openPublicRoom = (room) => {
    if (room.requires_code) {
      setCodeRoom(room);
      setCodeInput("");
      setCodeOpen(true);
    } else {
      nav(`/room/${room.id}`);
    }
  };

  const submitAccessCode = async (e) => {
    e.preventDefault();
    if (!codeRoom) return;
    setCodeSubmitting(true);
    try {
      await api.post(`/rooms/${codeRoom.id}/access`, { code: codeInput });
      toast.success("Access granted");
      setCodeOpen(false);
      nav(`/room/${codeRoom.id}`);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setCodeSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grain">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Hero strip */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
          <div>
            <div className="text-xs tracking-[0.25em] uppercase text-fuchsia-400 font-semibold">The lobby</div>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">
              pick a door.
            </h1>
            <p className="text-zinc-400 mt-3 max-w-xl">
              Open worldwide rooms below — or <span className="text-fuchsia-300">create a mini room</span> for up to 4 people.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Dialog open={joinOpen} onOpenChange={setJoinOpen}>
              <DialogTrigger asChild>
                <button data-testid="join-code-btn" className="ghost-btn inline-flex items-center gap-2">
                  <KeyRound className="w-4 h-4" /> Join by code
                </button>
              </DialogTrigger>
              <DialogContent className="bg-[#121215] border-white/10 text-white">
                <DialogHeader>
                  <DialogTitle className="font-display">Enter the password</DialogTitle>
                  <DialogDescription className="text-zinc-400">
                    Paste the 6-character invite code from your friend.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={joinMini} className="space-y-4 mt-2">
                  <input data-testid="join-code-input" required placeholder="e.g. A1B2C3" className="text-input uppercase tracking-[0.3em] text-center text-lg" value={joinCode} onChange={(e) => setJoinCode(e.target.value)} maxLength={8} />
                  <button data-testid="join-code-submit" disabled={joining} className="neon-btn w-full">
                    {joining ? "Knocking..." : "Slip inside"}
                  </button>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <button data-testid="create-mini-btn" className="neon-btn inline-flex items-center gap-2">
                  <Plus className="w-4 h-4" /> Create mini room
                </button>
              </DialogTrigger>
              <DialogContent className="bg-[#121215] border-white/10 text-white">
                <DialogHeader>
                  <DialogTitle className="font-display">Start a mini room</DialogTitle>
                  <DialogDescription className="text-zinc-400">
                    Invite-only. Max 4 people. Vanishes when everyone leaves.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={createMini} className="space-y-4 mt-2">
                  <input data-testid="create-name-input" required placeholder="Room name" className="text-input" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} />
                  <input data-testid="create-topic-input" placeholder="Topic (optional)" className="text-input" value={createForm.topic} onChange={(e) => setCreateForm({ ...createForm, topic: e.target.value })} />
                  <textarea data-testid="create-desc-input" placeholder="Description (optional)" className="text-input min-h-[90px] resize-none" value={createForm.description} onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })} />
                  <button data-testid="create-submit-btn" disabled={creating} className="neon-btn w-full">
                    {creating ? "Lighting the lamps..." : "Open the room"}
                  </button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* My mini rooms */}
        {myRooms.length > 0 && (
          <section className="mb-12">
            <div className="flex items-center gap-2 mb-4">
              <Lock className="w-4 h-4 text-fuchsia-400" />
              <h2 className="font-display text-xl font-bold tracking-tight">Your mini rooms</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {myRooms.map((r) => (
                <MiniCard key={r.id} room={r} onOpen={() => nav(`/room/${r.id}`)} />
              ))}
            </div>
          </section>
        )}

        {/* Public rooms */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Globe2 className="w-4 h-4 text-cyan-400" />
            <h2 className="font-display text-xl font-bold tracking-tight">Worldwide rooms</h2>
          </div>
          {loading ? (
            <div className="text-zinc-500">Loading rooms...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {publicRooms.map((r, i) => (
                <PublicCard key={r.id} room={r} index={i} onOpen={() => openPublicRoom(r)} />
              ))}
            </div>
          )}
        </section>
      </main>

      {/* Access-code dialog for code-gated public rooms (DownLow etc.) */}
      <Dialog open={codeOpen} onOpenChange={setCodeOpen}>
        <DialogContent className="bg-[#121215] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-display">
              {codeRoom ? `Enter ${codeRoom.name}` : "Enter room"}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              This room is code-invite only. Ask a regular for the word.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitAccessCode} className="space-y-4 mt-2">
            <input
              data-testid="access-code-input"
              required
              className="text-input uppercase tracking-[0.3em] text-center text-lg"
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value)}
              placeholder="the word"
              maxLength={24}
              autoFocus
            />
            <button data-testid="access-code-submit" disabled={codeSubmitting} className="neon-btn w-full">
              {codeSubmitting ? "Checking…" : "Unlock"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PublicCard({ room, onOpen, index }) {
  const tall = index % 5 === 0;
  const locked = !!room.requires_code;
  const isGeo = room.mode === "geo";
  const expiresAt = room.expires_at ? new Date(room.expires_at) : null;
  const daysLeft = expiresAt ? Math.max(0, Math.ceil((expiresAt - Date.now()) / (1000 * 60 * 60 * 24))) : null;
  return (
    <button
      data-testid={`public-room-card-${room.id}`}
      onClick={onOpen}
      className={`relative overflow-hidden rounded-3xl border ${expiresAt ? "border-fuchsia-500/30" : "border-white/5"} bg-[#121215] text-left card-lift group ${tall ? "md:row-span-1" : ""}`}
    >
      {room.cover && (
        <div className="absolute inset-0">
          <img src={room.cover} alt="" className="w-full h-full object-cover opacity-40 group-hover:opacity-55 group-hover:scale-105 transition-all duration-500" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/80 to-transparent" />
        </div>
      )}
      <div className="relative p-6 min-h-[180px] flex flex-col justify-between">
        <div className="flex items-center justify-between gap-2">
          <span className={`text-[10px] tracking-[0.3em] uppercase rounded-full px-2.5 py-1 bg-black/40 border ${
            locked ? "text-fuchsia-300 border-fuchsia-500/30"
            : isGeo ? "text-cyan-300 border-cyan-500/30"
            : expiresAt ? "text-amber-300 border-amber-500/40"
            : "text-zinc-400 border-white/10"
          }`}>
            {locked ? "🔒 Code only" : isGeo ? "📍 GPS" : expiresAt ? "⏳ Limited" : room.topic || "Room"}
          </span>
          {locked ? (
            <span className="flex items-center gap-1.5 text-xs text-zinc-500">
              <Lock className="w-3 h-3" /> private circle
            </span>
          ) : (
            <span className="flex items-center gap-1.5 text-xs text-emerald-400">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
              {room.online_count} online
            </span>
          )}
        </div>
        <div className="mt-8">
          <div className="font-display text-2xl font-bold tracking-tight">{room.name}</div>
          <div className="text-sm text-zinc-400 mt-1.5 line-clamp-2">{room.description}</div>
          {expiresAt && (
            <div
              className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-full px-2.5 py-1"
              data-testid={`room-expiry-${room.id}`}
            >
              <span className="font-bold">{daysLeft}</span> day{daysLeft === 1 ? "" : "s"} left · ends {expiresAt.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

function MiniCard({ room, onOpen }) {
  const copy = (e) => {
    e.stopPropagation();
    const url = `${window.location.origin}/invite/${room.invite_code}`;
    navigator.clipboard.writeText(url);
    toast.success("Invite link copied — paste anywhere");
  };
  const slots = Array.from({ length: 4 });
  const filled = room.members?.length || 0;
  return (
    <div
      data-testid={`mini-room-card-${room.id}`}
      onClick={onOpen}
      className="cursor-pointer rounded-3xl border border-white/5 bg-gradient-to-br from-[#1a1a20] to-[#0b0b0d] p-5 card-lift"
    >
      <div className="flex items-center justify-between">
        <span className="text-[10px] tracking-[0.3em] uppercase text-fuchsia-300 border border-fuchsia-500/30 rounded-full px-2.5 py-1 bg-fuchsia-500/5">
          Mini · {filled}/4
        </span>
        <button onClick={copy} data-testid={`copy-code-${room.id}`} className="text-xs text-zinc-400 hover:text-white inline-flex items-center gap-1">
          <Copy className="w-3 h-3" /> {room.invite_code}
        </button>
      </div>
      <div className="mt-4 font-display text-xl font-bold">{room.name}</div>
      {room.description && <div className="text-xs text-zinc-500 mt-1 line-clamp-1">{room.description}</div>}
      <div className="mt-5 flex items-center gap-1.5">
        {slots.map((_, i) => (
          <div
            key={i}
            className={`h-2 flex-1 rounded-full ${i < filled ? "bg-gradient-to-r from-[#e81cff] to-[#ff2a5f]" : "bg-white/5"}`}
          />
        ))}
      </div>
      <div className="mt-3 text-xs text-zinc-500 flex items-center gap-1">
        <Users className="w-3 h-3" /> {filled} of 4 seats filled
      </div>
    </div>
  );
}
